import pc from 'picocolors';
import { EXIT_CLI_OUTDATED } from './constants.js';

/**
 * Output conventions:
 *  - --json: machine-readable JSON only on stdout (single object, or one
 *    object per line for progress), nothing decorative.
 *  - human: branded, colored, to stdout; progress/log noise to stderr.
 */
export interface Io {
  json: boolean;
  productName: string;
}

export function emitJson(obj: unknown): void {
  process.stdout.write(`${JSON.stringify(obj)}\n`);
}

/** Print a human line (suppressed in --json mode). */
export function human(io: Io, line = ''): void {
  if (!io.json) process.stdout.write(`${line}\n`);
}

/** Progress/log noise — stderr in human mode, suppressed in --json mode. */
export function note(io: Io, line: string): void {
  if (!io.json) process.stderr.write(`${pc.dim(line)}\n`);
}

export function ok(io: Io, line: string): void {
  human(io, `${pc.green('✔')} ${line}`);
}

export function warn(io: Io, line: string): void {
  if (!io.json) process.stderr.write(`${pc.yellow('!')} ${line}\n`);
}

let jsonMode = false;

/** Set from commander's preAction hook — the HTTP client needs to know the
 * output mode for its one hard-stop (see exitCliOutdated) without every
 * command threading Io into it. */
export function setJsonMode(on: boolean): void {
  jsonMode = on;
}

/**
 * The server refused this CLI version (426 + cli_outdated). Handled at the
 * client choke point rather than per command: most commands catch ApiError
 * and re-fail with generic codes, but agents need the stable CLI_OUTDATED
 * code and exit status 3 so they can update the CLI and retry themselves.
 */
export function exitCliOutdated(message: string): never {
  if (jsonMode) {
    emitJson({ ok: false, code: 'CLI_OUTDATED', error: message });
  } else {
    process.stderr.write(`${pc.red('✘')} ${message}\n`);
  }
  process.exit(EXIT_CLI_OUTDATED);
}

/** Fail a command: JSON error object or red message, then exit. */
export function fail(io: Io, message: string, exitCode = 1, code = 'ERROR'): never {
  if (io.json) {
    emitJson({ ok: false, code, error: message });
  } else {
    process.stderr.write(`${pc.red('✘')} ${message}\n`);
  }
  process.exit(exitCode);
}
